// AI-CONTEXT: See docs/features/doelgroep-covariates.md
// Covariate axis (ADR-009): age / gender / education measured per individual,
// SEPARATE from the doelgroep taxonomy. Standardised value lists so the same
// term means the same thing across organizations.

import { EDUCATION_LEVEL_OPTIONS } from '../participationHelpers/participationOptions'

// ─── Gender ─────────────────────────────────────────────────────────────────

export const genderOptions = ['man', 'vrouw', 'anders', 'onbekend']

export const genderLabels = {
  man: 'Man',
  vrouw: 'Vrouw',
  anders: 'Anders',
  onbekend: 'Onbekend',
}

// ─── Education ────────────────────────────────────────────────────────────────
// Reuse the existing participation ladder — do NOT introduce a second list.
export const educationOptions = [...EDUCATION_LEVEL_OPTIONS, 'Onbekend']

// ─── Age bands ────────────────────────────────────────────────────────────────
// Decided sector-variant boundaries (ADR-009). Independent of the chosen
// doelgroep: a persona in any doelgroep falls into exactly one age band.

/**
 * @typedef {object} AgeBand
 * @property {string} key - Stable band key
 * @property {string} label - Display label
 * @property {number} min - Lower bound in years (inclusive)
 * @property {number} max - Upper bound in years (inclusive)
 */

/** @type {AgeBand[]} */
export const ageBands = [
  { key: 'kind', label: 'Kind', min: 0, max: 11 },
  { key: 'jongere', label: 'Jongere', min: 12, max: 26 },
  { key: 'volwassene', label: 'Volwassene', min: 27, max: 66 },
  { key: 'oudere', label: 'Oudere', min: 67, max: Infinity },
]

/**
 * Map a birthdate to its age band key. Returns 'onbekend' when the birthdate is
 * missing, unparseable or in the future. Pass an explicit reference date for
 * deterministic tests.
 *
 * @param {string | Date | null | undefined} birthdate
 * @param {Date} [referenceDate]
 * @returns {string} An age-band key, or 'onbekend'
 */
export const ageToBand = (birthdate, referenceDate = new Date()) => {
  if (!birthdate) return 'onbekend'

  const born = birthdate instanceof Date ? birthdate : new Date(birthdate)
  if (Number.isNaN(born.getTime())) return 'onbekend'

  let age = referenceDate.getFullYear() - born.getFullYear()
  const monthDiff = referenceDate.getMonth() - born.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < born.getDate())) {
    age -= 1
  }

  if (age < 0) return 'onbekend'

  const band = ageBands.find((b) => age >= b.min && age <= b.max)
  return band ? band.key : 'onbekend'
}

export default ageToBand
